import React, { useState } from 'react';
import { Button, Modal, Form, Input, Upload, message, Tooltip } from 'antd';
import { MessageOutlined, BugOutlined, ToolOutlined, BulbOutlined, InboxOutlined } from '@ant-design/icons';
import API from 'Util/Axios';
import { BASE_URL } from 'Constants';
import { TIPO_META } from './shared';
import { getRouteLabel } from './routeLabels';

const { TextArea } = Input;
const { Dragger } = Upload;

// Read a File/Blob as a base64 data-URL (same technique as CustomUpload's getBase64)
const readAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

// ---------------------------------------------------------------------------
// TipoSelector — 3 clickable card-style buttons for tipo selection
// ---------------------------------------------------------------------------
function TipoSelector({ value, onChange }) {
  const options = [
    { key: 'falla', label: 'Bug', color: '#ff4d4f', bg: '#fff1f0', icon: <BugOutlined /> },
    { key: 'mejora', label: 'Mejora', color: '#1677ff', bg: '#e6f4ff', icon: <ToolOutlined /> },
    { key: 'nueva_funcion', label: 'Nueva función', color: '#52c41a', bg: '#f6ffed', icon: <BulbOutlined /> },
  ];

  return (
    <div style={{ display: 'flex', gap: 8 }}>
      {options.map((opt) => {
        const selected = value === opt.key;
        return (
          <div
            key={opt.key}
            onClick={() => onChange(opt.key)}
            style={{
              flex: 1,
              border: selected ? `2px solid ${opt.color}` : '2px solid #d9d9d9',
              borderRadius: 8,
              padding: '10px 8px',
              cursor: 'pointer',
              textAlign: 'center',
              background: selected ? opt.bg : '#fafafa',
              color: selected ? opt.color : '#595959',
              fontWeight: selected ? 600 : 400,
              transition: 'all 0.15s',
              userSelect: 'none',
            }}
          >
            <div style={{ fontSize: 20 }}>{opt.icon}</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>{opt.label}</div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FeedbackButton — global floating feedback button with creation modal
// ---------------------------------------------------------------------------
export default function FeedbackButton() {
  const [visible, setVisible] = useState(false);
  const [tipo, setTipo] = useState(null);
  const [descripcion, setDescripcion] = useState('');
  const [capturaFile, setCapturaFile] = useState(null);
  const [loading, setLoading] = useState(false);

  // Derive module label on each open (window.location.pathname at that moment)
  const modulo = visible ? getRouteLabel(window.location.pathname) : '';

  const handleOpen = () => {
    setVisible(true);
  };

  const handleClose = () => {
    setVisible(false);
    setTipo(null);
    setDescripcion('');
    setCapturaFile(null);
  };

  const handleSubmit = async () => {
    if (!tipo || !descripcion.trim()) return;
    setLoading(true);
    let capturaUrl = null;

    if (capturaFile) {
      try {
        const { data: fileData } = await API.post('file', {
          content: capturaFile.base64,
          fileName: capturaFile.name,
          folder: 'control-cambios',
        });
        capturaUrl = `${BASE_URL}/api/file/${fileData._id}?sendFile=1&token=${localStorage.getItem('token')}`;
      } catch (err) {
        message.error('No se pudo subir el archivo. Intentá de nuevo.');
        setLoading(false);
        return;
      }
    }

    try {
      await API.post('control-cambios', {
        tipo,
        descripcion: descripcion.trim(),
        modulo: getRouteLabel(window.location.pathname),
        captura: capturaUrl,
      });

      message.success('Reporte enviado. Te notificaremos cuando haya novedades.');
      handleClose();
    } catch (err) {
      message.error('No se pudo guardar el reporte. Intentá de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const isSubmitDisabled = !tipo || !descripcion.trim() || loading;

  return (
    <>
      {/* Floating trigger button */}
      <Tooltip title="¿Algo que mejorar o reportar?" placement="left">
        <Button
          type="primary"
          shape="circle"
          size="large"
          icon={<MessageOutlined />}
          onClick={handleOpen}
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000,
            backgroundColor: '#185FA5',
            borderColor: '#185FA5',
            boxShadow: '0 4px 12px rgba(24, 95, 165, 0.4)',
          }}
        />
      </Tooltip>

      {/* Report creation modal */}
      <Modal
        title="Reportar un problema o sugerencia"
        open={visible}
        onCancel={handleClose}
        footer={null}
        destroyOnClose
        width={520}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
          {/* Tipo selector */}
          <div>
            <div style={{ marginBottom: 6, fontWeight: 500 }}>Tipo de reporte</div>
            <TipoSelector value={tipo} onChange={setTipo} />
          </div>

          {/* Módulo detectado (read-only) */}
          <div>
            <div style={{ marginBottom: 6, fontWeight: 500 }}>Módulo detectado</div>
            <div
              style={{
                padding: '6px 12px',
                background: '#f5f5f5',
                borderRadius: 6,
                color: '#595959',
                fontSize: 13,
              }}
            >
              {modulo || getRouteLabel(window.location.pathname)}
            </div>
          </div>

          {/* Descripción */}
          <div>
            <div style={{ marginBottom: 6, fontWeight: 500 }}>
              Descripción <span style={{ color: '#ff4d4f' }}>*</span>
            </div>
            <TextArea
              rows={4}
              placeholder="Describí el problema o la mejora que querés reportar..."
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              maxLength={2000}
              showCount
            />
          </div>

          {/* Captura (optional) */}
          <div>
            <div style={{ marginBottom: 6, fontWeight: 500 }}>
              Captura de pantalla{' '}
              <span style={{ color: '#8c8c8c', fontWeight: 400, fontSize: 12 }}>(opcional)</span>
            </div>
            <Dragger
              accept="image/*,.pdf"
              maxCount={1}
              fileList={capturaFile ? [{ uid: '1', name: capturaFile.name, status: 'done' }] : []}
              customRequest={async ({ file, onSuccess, onError }) => {
                try {
                  const base64 = await readAsBase64(file);
                  setCapturaFile({ name: file.name, contentType: file.type, base64 });
                  onSuccess('ok');
                } catch (err) {
                  message.error('No se pudo leer el archivo. Intentá de nuevo.');
                  onError(err);
                }
              }}
              onRemove={() => setCapturaFile(null)}
              showUploadList={{ showPreviewIcon: false }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Hacé clic o arrastrá un archivo aquí</p>
              <p className="ant-upload-hint">Imágenes o PDF, 1 archivo máximo</p>
            </Dragger>
            {capturaFile && (
              <div style={{ marginTop: 4, fontSize: 12, color: '#595959' }}>
                Archivo seleccionado: {capturaFile.name}
              </div>
            )}
          </div>

          {/* Submit button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={handleClose} disabled={loading}>
              Cancelar
            </Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              loading={loading}
              style={{ backgroundColor: '#185FA5', borderColor: '#185FA5' }}
            >
              Enviar reporte
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
